import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { candidateLogin } from '../../utils/candidateApi';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import BrandBadge from '../../components/brand/BrandBadge';
import { applyServerFieldErrors } from '../../utils/errors';
import { formatCandidateLoginError } from '../../utils/candidateAuthErrors';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Only follow in-app destinations handed over by the 401 interceptor. */
function safeNext(value) {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/portal';
}

export default function CandidateLogin() {
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const expired = searchParams.get('expired') === '1';
  const { register, handleSubmit, setError, formState: { errors } } = useForm();

  const onSubmit = async ({ email, password }) => {
    setLoading(true);
    setFormError(null);
    try {
      await candidateLogin(email.trim(), password);  // token is held in memory only
      navigate(safeNext(searchParams.get('next')), { replace: true });
    } catch (err) {
      const applied = applyServerFieldErrors(err, setError, { fields: ['email', 'password'] });
      if (!applied.length) setFormError(formatCandidateLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-8 sm:py-10" data-testid="candidate-login-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-5 sm:mb-6">
          <BrandBadge />
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-4">Candidate Portal</h1>
          <p className="text-slate-500 mt-2">Sign in with your candidate account</p>
        </div>

        {expired && !formError && (
          <div role="status" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
            <p className="text-sm font-semibold text-amber-900">Your session expired</p>
            <p className="mt-1 text-sm text-amber-800">Sign in again to pick up where you left off.</p>
          </div>
        )}

        {formError && (
          <div
            role="alert"
            data-testid="candidate-login-error"
            className="mb-4 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3.5"
          >
            <p className="text-sm font-semibold text-danger-800">We couldn&apos;t sign you in</p>
            <p className="mt-1 text-sm text-danger-700">{formError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="surface-elevated p-8 space-y-4" data-testid="candidate-login-form">
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            data-testid="candidate-login-email"
            error={errors.email?.message}
            {...register('email', {
              required: 'Enter the email you applied with',
              pattern: { value: EMAIL_PATTERN, message: 'Enter a valid email address' },
            })}
          />
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            data-testid="candidate-login-password"
            error={errors.password?.message}
            {...register('password', { required: 'Enter your password' })}
          />
          <div className="flex justify-end -mt-1">
            <Link to="/candidate/forgot-password" className="text-sm text-brand-600 hover:underline">Forgot password?</Link>
          </div>
          <Button type="submit" className="w-full" loading={loading} data-testid="candidate-login-submit">
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
        <p className="text-center text-sm text-slate-500 mt-4">
          New here? <Link to="/candidate/signup" className="text-brand-600 hover:underline">Create candidate account</Link>
        </p>
      </div>
    </div>
  );
}
